import React, { useEffect, useRef } from 'react';
import { NEWS_ITEMS, SHOW_NEWS_CAROUSEL, type NewsItem } from '../../data/news';
import '../../styles/news.css';

const openNews = (slug: string, skip = false) => {
  if (skip) return;
  const url = `${window.location.origin}${window.location.pathname}#/news/${slug}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const formatDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const NewsFeatured: React.FC<{ item: NewsItem }> = ({ item }) => (
  <article className="news-featured-wrap">
    <button
      type="button"
      className="news-featured"
      onClick={() => openNews(item.slug)}
      aria-label={`Open news: ${item.title}`}
    >
      <div className={`news-featured__media news-featured__media--${item.imageFit || 'cover'}`}>
        <img src={item.image} alt={item.imageAlt} />
      </div>
      <div className="news-featured__body">
        <time className="news-featured__date" dateTime={item.date}>
          {formatDate(item.date)}
        </time>
        <h3 className="news-featured__title">{item.title}</h3>
        <p className="news-featured__excerpt">{item.excerpt}</p>
        <span className="news-featured__cta">Read more</span>
      </div>
    </button>
  </article>
);

const NewsCarousel: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const startPos = useRef(0);
  const scrollPos = useRef(0);

  const tripleNews = [...NEWS_ITEMS, ...NEWS_ITEMS, ...NEWS_ITEMS];

  useEffect(() => {
    const slider = sliderRef.current;
    const track = trackRef.current;

    if (!slider || !track) return;

    const getSetWidth = () => {
      const slides = slider.querySelectorAll<HTMLElement>('.news-slide');
      if (!slides.length) return 1440;

      const first = slides[0];
      const cardWidth = first.offsetWidth;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return (cardWidth + gap) * NEWS_ITEMS.length;
    };

    const createKeyframeAnimation = (startPosition: number) => {
      const existingStyle = document.getElementById('news-carousel-keyframes');
      if (existingStyle) {
        existingStyle.remove();
      }

      const setWidth = getSetWidth();
      const normalizedPosition = ((startPosition % setWidth) + setWidth) % setWidth;

      const styleSheet = document.createElement('style');
      styleSheet.id = 'news-carousel-keyframes';
      styleSheet.textContent = `
        @keyframes news-scroll-dynamic {
          0% { transform: translateX(${normalizedPosition}px); }
          100% { transform: translateX(${normalizedPosition - setWidth}px); }
        }
      `;
      document.head.appendChild(styleSheet);
    };

    const disableAnimation = () => {
      const transform = window.getComputedStyle(track).getPropertyValue('transform');
      const matrix = new DOMMatrix(transform);
      const currentTranslateX = matrix.m41;

      track.style.animation = 'none';
      void track.offsetHeight;
      track.style.transform = `translateX(${currentTranslateX}px)`;

      return currentTranslateX;
    };

    const enableAnimation = (currentPosition: number) => {
      createKeyframeAnimation(currentPosition);
      track.style.transform = '';
      track.style.animation = 'news-scroll-dynamic 40s linear infinite';
    };

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      didDrag.current = false;
      slider.classList.add('is-active');
      track.classList.add('is-grabbing');
      startPos.current = e.clientX;
      scrollPos.current = disableAnimation();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const diff = e.clientX - startPos.current;
      if (Math.abs(diff) > 6) {
        didDrag.current = true;
      }
      track.style.transform = `translateX(${scrollPos.current + diff}px)`;
    };

    const handleMouseUp = () => {
      if (!isDragging.current) return;

      const transform = window.getComputedStyle(track).getPropertyValue('transform');
      const matrix = new DOMMatrix(transform);
      const finalPosition = matrix.m41;

      isDragging.current = false;
      slider.classList.remove('is-active');
      track.classList.remove('is-grabbing');

      setTimeout(() => {
        enableAnimation(finalPosition);
      }, 50);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      isDragging.current = true;
      didDrag.current = false;
      slider.classList.add('is-active');
      track.classList.add('is-grabbing');
      startPos.current = e.touches[0].clientX;
      scrollPos.current = disableAnimation();
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging.current || e.touches.length !== 1) return;
      e.preventDefault();
      const diff = e.touches[0].clientX - startPos.current;
      if (Math.abs(diff) > 6) {
        didDrag.current = true;
      }
      track.style.transform = `translateX(${scrollPos.current + diff}px)`;
    };

    createKeyframeAnimation(0);
    track.style.animation = 'news-scroll-dynamic 40s linear infinite';

    slider.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);
    slider.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      slider.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
      slider.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);

      document.getElementById('news-carousel-keyframes')?.remove();
    };
  }, []);

  return (
    <div className="news-slider" ref={sliderRef}>
      <div className="news-slide-track" ref={trackRef}>
        {tripleNews.map((item, index) => (
          <article key={`${item.slug}-${index}`} className="news-slide">
            <button
              type="button"
              className="news-card"
              onClick={() => openNews(item.slug, didDrag.current)}
              aria-label={`Open news: ${item.title}`}
            >
              <div className="news-card__image-wrap">
                <img src={item.image} alt={item.imageAlt} draggable={false} />
              </div>
              <div className="news-card__body">
                <time className="news-card__date" dateTime={item.date}>
                  {formatDate(item.date)}
                </time>
                <h3 className="news-card__title">{item.title}</h3>
                <p className="news-card__excerpt">{item.excerpt}</p>
              </div>
            </button>
          </article>
        ))}
      </div>
    </div>
  );
};

const NewsSection: React.FC = () => {
  const featured = NEWS_ITEMS[0];

  return (
    <section id="news" className="news-section">
      <div className={`news-section__container${SHOW_NEWS_CAROUSEL ? '' : ' news-section__container--featured'}`}>
        <h2 className="news-section__title">Latest from FTDiam</h2>
        {SHOW_NEWS_CAROUSEL || !featured ? (
          <NewsCarousel />
        ) : (
          <NewsFeatured item={featured} />
        )}
      </div>
    </section>
  );
};

export default NewsSection;
